import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Drawer, useMantineTheme } from "@mantine/core";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import OrdersTable from "@/components/organisms/OrdersTable";
import {
  DEFAULT_ORDER_COLUMNS,
  DEFAULT_ORDER_MOBILE_HEADER_COLUMNS,
} from "@/components/organisms/OrdersTable/const/default";
import OrdersFilter from "@/components/organisms/OrdersTable/OrdersFilter";
import { effectiveOrderTypes } from "@/components/organisms/OrdersTable/const/orderTypes";
import useOrdersFilterSync from "@/components/organisms/OrdersTable/OrdersFilter/useOrdersFilterSync";
import OrdersSort from "@/components/organisms/OrdersTable/OrdersSort";
import OrderCalendar from "@/components/organisms/OrderCalendar";
import SegmentedControls from "@/components/atoms/SegmentedControls";
import CTA from "@/components/atoms/CTA";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import styles from "./styles.module.css";
import { useAuth } from "@/contexts/AuthContext";
import { useGetCompanyById } from "@/services/Company";
import { useOrderStore } from "@/stores/ordersStore";
import { SubHeader } from "@/components/molecules/SubHeader";

type OrdersView = "table" | "calendar";

export default function MyOrders() {
  const { t } = useTranslation();
  const { currentCompany } = useAuth();
  const { data: company } = useGetCompanyById(currentCompany?.companyId as number, {
    enabled: !!currentCompany?.companyId,
  });
  const [totalItems, setTotalItems] = useState(0);
  const [view, setView] = useState<OrdersView>("table");
  const [filtersOpened, { open: openFilters, close: closeFilters }] = useDisclosure(false);

  // The drawer is only for narrow screens. Close it when the screen grows into the side panel's range,
  // otherwise it stays "open" (and keeps the page scroll-locked) while being hidden.
  const theme = useMantineTheme();
  const isWide = useMediaQuery(`(min-width: ${theme.breakpoints.md})`);
  useEffect(() => {
    if (isWide) closeFilters();
  }, [isWide]);

  // Mounted here, not in the filter inputs: the inputs live in a drawer on small screens.
  useOrdersFilterSync();

  const statuses = useOrderStore((state) => state.statuses);
  const orderTypes = useOrderStore((state) => state.orderTypes);
  const salesItemId = useOrderStore((state) => state.salesItemId);
  const onlyConfirmedDespiteWarning = useOrderStore((state) => state.onlyConfirmedDespiteWarning);
  const activeFilterCount = [
    statuses.length > 0,
    effectiveOrderTypes(orderTypes) !== null,
    salesItemId !== null,
    onlyConfirmedDespiteWarning,
  ].filter(Boolean).length;

  // The calendar only exists for companies with order planning; everyone else gets the table alone.
  const canShowCalendar = !!company?.isOrderPlanningEnabled;
  const activeView: OrdersView = canShowCalendar ? view : "table";

  const toolbar = (
    <div className={styles.toolbar}>
      {canShowCalendar && (
        <SegmentedControls
          aria-label={t("orders:listView")}
          data-testid="orders-view-switch"
          value={activeView}
          onChange={(value) => setView(value as OrdersView)}
          data={[
            { value: "table", label: t("orders:viewTable") },
            { value: "calendar", label: t("orders:viewCalendar") },
          ]}
        />
      )}
      <div className={styles.toolbarSpacer} />
      {activeView === "table" && (
        <>
          <Box hiddenFrom="md">
            {/* Icon-only, so the label and the active count are for screen readers; the badge shows the count */}
            <button
              type="button"
              className={styles.filtersButton}
              onClick={openFilters}
              aria-label={`${t("common:filters")}${activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}`}
              title={t("common:filters")}
              data-testid="orders-open-filters-button"
            >
              <SVGAsset name="sliders" alt="" width="20" height="20" />
              {activeFilterCount > 0 && (
                <span className={styles.filtersBadge} aria-hidden="true" data-testid="orders-filters-count">
                  {activeFilterCount}
                </span>
              )}
            </button>
          </Box>
          <OrdersSort />
        </>
      )}
    </div>
  );

  return (
    <>
      <title>{`Reviver - ${t("dashboard:myOrders")}`}</title>
      <meta name="description" content={`Reviver - ${t("dashboard:myOrders")}`} />
      <SubHeader title={t("navigation:orders")} count={totalItems} countLabel={t("common:orders")} />
      <Box className={styles.contentRoot}>
        <div className={styles.contentInner}>
          {/* One stable tree for both views, so the toolbar (and the view switch the user just used)
              is not remounted when the view changes. */}
          <div className={styles.layout}>
            {activeView === "table" && (
              <Box visibleFrom="md" className={styles.filterPanel}>
                <OrdersFilter />
              </Box>
            )}
            <div className={styles.tableColumn}>
              {toolbar}
              {activeView === "table" ? (
                <OrdersTable
                  visibleColumns={DEFAULT_ORDER_COLUMNS}
                  mobileHeaderColumns={DEFAULT_ORDER_MOBILE_HEADER_COLUMNS}
                  highlightActionRequired
                  onTotalItemsChange={setTotalItems}
                />
              ) : (
                <OrderCalendar />
              )}
            </div>
          </div>
        </div>
      </Box>

      <Drawer
        opened={filtersOpened}
        onClose={closeFilters}
        position="bottom"
        size="auto"
        title={t("common:filters")}
        hiddenFrom="md"
        data-testid="orders-filters-drawer"
      >
        <OrdersFilter scrollable={false} />
        {/* Filters apply live; this only closes the drawer. */}
        <div className={styles.drawerCta}>
          <CTA fullWidth size="lg" onClick={closeFilters}>
            {t("orders:showOrders", { count: totalItems })}
          </CTA>
        </div>
      </Drawer>
    </>
  );
}
